import React, { useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { formatDate, formatDateTime, formatMoney, formatNumber } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { Breadcrumb } from '../shared/Breadcrumb';

type Locale = 'hr' | 'en';

type IncludeRow = {
    id: string;
    source: 'live' | 'manual' | 'plan';
    label: string;
    book_eur: number;
    fine_g?: number;
    spot_eur?: number;
    note: string;
    included_in_sum: boolean;
};

type WarehouseRow = {
    warehouse_code: string;
    warehouse_name: string;
    ownership_type: string;
    group?: string;
    qty: number;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
};

type WarehouseGroup = {
    key: string;
    label: string;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
    source?: 'live' | 'manual' | 'both' | 'none';
    live_book_eur?: number;
    live_fine_g?: number;
    warehouses: WarehouseRow[];
};

type WholesaleWriteoffRow = {
    id: number;
    item_code: string | null;
    item_name: string | null;
    qty: number;
    fine_g: number;
    spot_eur: number;
    book_eur: number;
    reason: string | null;
    note: string | null;
    entered_at: string | null;
};

type ItemWholesale = {
    number: 1;
    label: string;
    qty: number;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
    breakdown: Array<{
        item_code: string;
        item_name: string | null;
        qty: number;
        fine_g: number;
        book_eur: number;
        spot_eur: number;
    }>;
    // N23 (zahtjev 1, mail UID 556, 11.09.2026): bruto prije repova + oduzeti write-offi.
    gross_book_eur: number;
    gross_fine_g: number;
    gross_spot_eur: number;
    writeoffs: {
        fine_g: number;
        spot_eur: number;
        note: string;
        rows: WholesaleWriteoffRow[];
    };
};

type ItemBranches = {
    number: 2;
    label: string;
    qty: number;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
    breakdown: Array<{
        warehouse_code: string;
        warehouse_name: string;
        qty: number;
        fine_g: number;
        book_eur: number;
        spot_eur: number;
        item_count: number;
    }>;
    note?: string;
};

type ItemLager = {
    number: '6a';
    label: string;
    qty: number;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
    breakdown: Array<{
        warehouse_code: string;
        warehouse_name: string;
        qty: number;
        fine_g: number;
        book_eur: number;
        spot_eur: number;
        item_count: number;
    }>;
    note: string;
};

type ItemAkcijsko = {
    number: '6b';
    label: string;
    qty: number;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
    breakdown: Array<{
        product_id: number | null;
        sku: string | null;
        name: string | null;
        qty: number;
        fine_g: number;
        book_eur: number;
        spot_eur: number;
    }>;
    note: string;
};

type ItemTransit = {
    number: 3;
    label: string;
    qty: number;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
    breakdown: Array<{
        warehouse_code: string;
        warehouse_name: string;
        item_code: string;
        item_name: string | null;
        qty_in_transit: number;
        fine_g: number;
        book_eur: number;
        spot_eur: number;
    }>;
};

type ItemSuppliers = {
    number: 4;
    label: string;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
    breakdown: Array<{
        id: string;
        source: 'manual' | 'plan';
        label: string;
        book_eur: number;
        fine_g: number;
        spot_eur: number;
        qty?: number;
        note: string;
        included_in_sum: boolean;
    }>;
    match?: {
        confirmed_l3_rows: number;
        matched_rows: number;
        matched_orders: number;
        skipped_unconfirmed: number;
        skipped_no_open_order: number;
        skipped_no_item_match: number;
        qty_capped_rows: number;
    };
    // N23 (zahtjev 4, mail UID 556, 11.09.2026): dnevna ručna potvrda ručnog dijela.
    last_verified_at: string | null;
    verification_warning: boolean;
};

type UndeliveredSplit = {
    label: string;
    order_count: number;
    remaining_eur: number;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
    days_eur: number | null;
    days_fine: number | null;
};

type ItemUndelivered = {
    number: 5;
    label: string;
    order_count: number;
    paid_full_count: number;
    paid_full_eur: number;
    remaining_eur: number;
    book_eur: number;
    fine_g: number;
    spot_eur: number;
    // N23 (zahtjev 5, mail UID 556, 11.09.2026): X dana prosječnog prometa.
    days_eur: number | null;
    days_fine: number | null;
    // N23 (zahtjev 3, mail UID 556, 11.09.2026): split po mjestu isporuke.
    a_isporuka: UndeliveredSplit;
    b_pohrana: UndeliveredSplit;
};

type CoverageData = {
    permissions: { can_edit: boolean };
    spot: {
        spot_date: string | null;
        price_eur_per_troy_oz: number;
        price_eur_per_g: number;
    };
    snapshots: {
        stock_batch_id: number | null;
        open_orders_snapshot_at: string | null;
    };
    unknown_warehouses?: { code: string; name: string; rows: number }[];
    items: {
        item_1_veleprodaja: ItemWholesale;
        item_2_poslovnice: ItemBranches;
        item_3_na_putu: ItemTransit;
        item_4_dobavljaci: ItemSuppliers;
        item_5_neisporucene: ItemUndelivered;
        item_6a_lager: ItemLager;
        item_6b_akcijsko: ItemAkcijsko;
    };
    coverage: {
        formula: string;
        book_eur: number;
        fine_g: number;
        spot_eur: number;
        total_assets_book_eur: number;
        total_assets_fine_g: number;
        total_assets_spot_eur: number;
        ratio_pct: number;
        ratio_fine_pct: number;
        ratio_book_pct: number;
        supplier_goods: { book_eur: number; fine_g: number; spot_eur: number; money_delta_eur: number; fixed_fine_g: number };
        delay_days: {
            date_from: string; date_to: string; period_days: number;
            ado_turnover_eur: number; ado_turnover_fine_g: number;
            ado_daily_eur: number; ado_daily_fine_g: number;
            days_eur: number | null; days_fine: number | null;
        };
    };
    informative: {
        note: string;
        stonex: {
            key: string;
            label: string;
            book_eur: number;
            fine_g: number;
            spot_eur: number;
            note: string;
            rows?: IncludeRow[];
        };
        potrazivanja: {
            key: string;
            label: string;
            book_eur: number;
            fine_g: number;
            spot_eur: number;
            breakdown?: {
                open_orders_remaining_eur: number;
                ira_b2b_open_eur: number;
                live_eur?: number;
                manual_eur?: number;
            };
            note: string;
            rows?: IncludeRow[];
        };
        lom: {
            key: string;
            label: string;
            book_eur: number;
            fine_g: number;
            spot_eur: number;
            note: string;
            rows?: IncludeRow[];
        };
        strana_skladista: {
            key: string;
            label: string;
            book_eur: number;
            fine_g: number;
            spot_eur: number;
            warehouses: WarehouseRow[];
            groups?: WarehouseGroup[];
            outside?: WarehouseGroup[];
            rows?: IncludeRow[];
            note: string;
        };
        gotovina: {
            key: string;
            label: string;
            book_eur: number;
            fine_g: number;
            spot_eur: number;
            breakdown?: {
                bank_accounts_eur: number;
                blagajne_eur: number;
                bank_accounts_m6_eur?: number;
                bank_accounts_manual_eur?: number;
                blagajne_m6_eur?: number;
                blagajne_manual_eur?: number;
            };
            note: string;
            rows?: IncludeRow[];
        };
        /**
         * m.40.4 F1 nastavak — Vedran 26.08.2026: zaliha koja fizički stoji u poslovnici
         * (isti obuhvat kao item_2_poslovnice) ali nije rezervirana ni za jednu narudžbu.
         * Čisto informativno, nema kvačica ni utjecaja na formulu pokrivenosti.
         */
        nekurentna: {
            book_eur: number;
            fine_g: number;
            spot_eur: number;
            qty: number;
            by_warehouse: Array<{
                warehouse_code: string;
                warehouse_name: string;
                qty: number;
                fine_g: number;
                book_eur: number;
                spot_eur: number;
                item_count: number;
            }>;
        };
    };
    retail_daily: {
        scope: 'all' | 'voditelj' | 'branch'; date_from: string; date_to: string;
        rows: Array<{ warehouse_code: string; warehouse_name: string; stock_eur: number; in_transit_eur: number; sales_eur: number; sales_fine_g: number; avg_daily_sales_eur: number; depletion_days: number | null }>;
        totals: { stock_eur: number; in_transit_eur: number; sales_eur: number; sales_fine_g: number };
    };
};

type ManualInput = {
    key: string;
    label: string;
    unit: string;
    value: number | string;
};

function round2(n: number): number {
    return Math.round(n * 100) / 100;
}

function round4(n: number): number {
    return Math.round(n * 10000) / 10000;
}

function applyInclude(data: CoverageData, id: string, included: boolean): CoverageData {
    const next: CoverageData = JSON.parse(JSON.stringify(data));
    const patch = (rows: Array<{ id: string; included_in_sum: boolean }> | undefined) => {
        if (!rows) return;
        const row = rows.find((r) => r.id === id);
        if (row) row.included_in_sum = included;
    };
    patch(next.informative.stonex.rows);
    patch(next.informative.potrazivanja.rows);
    patch(next.informative.lom.rows);
    patch(next.informative.gotovina.rows);
    patch(next.informative.strana_skladista.rows);
    patch(next.items.item_4_dobavljaci.breakdown);

    let i4Book = 0;
    let i4Fine = 0;
    let i4Spot = 0;
    for (const r of next.items.item_4_dobavljaci.breakdown) {
        if (!r.included_in_sum) continue;
        i4Book += r.book_eur;
        i4Fine += r.fine_g;
        i4Spot += r.spot_eur;
    }
    next.items.item_4_dobavljaci.book_eur = round2(i4Book);
    next.items.item_4_dobavljaci.fine_g = round4(i4Fine);
    next.items.item_4_dobavljaci.spot_eur = round2(i4Spot);

    const i1 = next.items.item_1_veleprodaja;
    const i2 = next.items.item_2_poslovnice;
    const i3 = next.items.item_3_na_putu;
    const i5 = next.items.item_5_neisporucene;
    const i6a = next.items.item_6a_lager;
    const i6b = next.items.item_6b_akcijsko;
    // N23 (zahtjev 6a/6b, mail UID 556, 11.09.2026): lager i akcijsko ulaze u glavnu formulu.
    const assetsBook = round2(i1.book_eur + i2.book_eur + i3.book_eur + i6a.book_eur + i6b.book_eur);
    const assetsFine = round4(i1.fine_g + i2.fine_g + i3.fine_g + i6a.fine_g + i6b.fine_g);
    const assetsSpot = round2(i1.spot_eur + i2.spot_eur + i3.spot_eur + i6a.spot_eur + i6b.spot_eur);
    next.coverage.total_assets_book_eur = assetsBook;
    next.coverage.total_assets_fine_g = assetsFine;
    next.coverage.total_assets_spot_eur = assetsSpot;
    next.coverage.book_eur = round2(assetsBook - i5.book_eur);
    next.coverage.fine_g = round4(assetsFine - i5.fine_g);
    next.coverage.spot_eur = round2(assetsSpot - i5.spot_eur);
    next.coverage.ratio_pct = i5.spot_eur > 0 ? round2((assetsSpot / i5.spot_eur) * 100) : assetsSpot > 0 ? 100 : 0;
    next.coverage.ratio_fine_pct = i5.fine_g > 0 ? round2((assetsFine / i5.fine_g) * 100) : assetsFine > 0 ? 100 : 0;
    next.coverage.ratio_book_pct = i5.book_eur > 0 ? round2((assetsBook / i5.book_eur) * 100) : assetsBook > 0 ? 100 : 0;

    if (next.informative.stonex.rows) {
        let book = 0;
        let fine = 0;
        let spot = 0;
        for (const r of next.informative.stonex.rows) {
            if (!r.included_in_sum) continue;
            book += r.book_eur;
            fine += r.fine_g ?? 0;
            spot += r.spot_eur ?? 0;
        }
        next.informative.stonex.book_eur = round2(book);
        next.informative.stonex.fine_g = round4(fine);
        next.informative.stonex.spot_eur = round2(spot);
    }
    if (next.informative.potrazivanja.rows) {
        let book = 0;
        let fine = 0;
        let spot = 0;
        for (const r of next.informative.potrazivanja.rows) {
            if (!r.included_in_sum) continue;
            book += r.book_eur;
            fine += r.fine_g ?? 0;
            spot += r.spot_eur ?? r.book_eur;
        }
        next.informative.potrazivanja.book_eur = round2(book);
        next.informative.potrazivanja.fine_g = round4(fine);
        next.informative.potrazivanja.spot_eur = round2(spot);
    }
    if (next.informative.lom.rows) {
        let book = 0;
        let fine = 0;
        let spot = 0;
        for (const r of next.informative.lom.rows) {
            if (!r.included_in_sum) continue;
            book += r.book_eur;
            fine += r.fine_g ?? 0;
            spot += r.spot_eur ?? r.book_eur;
        }
        next.informative.lom.book_eur = round2(book);
        next.informative.lom.fine_g = round4(fine);
        next.informative.lom.spot_eur = round2(spot);
    }
    if (next.informative.gotovina.rows) {
        let banks = 0;
        let blagajne = 0;
        for (const r of next.informative.gotovina.rows) {
            if (!r.included_in_sum) continue;
            if (r.id.startsWith('gotovina_banks_')) banks += r.book_eur;
            else blagajne += r.book_eur;
        }
        const total = round2(banks + blagajne);
        next.informative.gotovina.book_eur = total;
        next.informative.gotovina.spot_eur = total;
        const spotG = data.spot.price_eur_per_g;
        next.informative.gotovina.fine_g = spotG > 0 ? round4(total / spotG) : 0;
        if (next.informative.gotovina.breakdown) {
            next.informative.gotovina.breakdown.bank_accounts_eur = round2(banks);
            next.informative.gotovina.breakdown.blagajne_eur = round2(blagajne);
        }
    }
    if (next.informative.strana_skladista.rows && next.informative.strana_skladista.groups) {
        const acc: Record<string, { book: number; fine: number; spot: number; liveOn: boolean; manualOn: boolean }> = {
            bugarska: { book: 0, fine: 0, spot: 0, liveOn: false, manualOn: false },
            slovenija: { book: 0, fine: 0, spot: 0, liveOn: false, manualOn: false },
        };
        for (const r of next.informative.strana_skladista.rows) {
            const key = r.id.includes('bugarska') ? 'bugarska' : r.id.includes('slovenija') ? 'slovenija' : '';
            if (!key || !r.included_in_sum) continue;
            acc[key].book += r.book_eur;
            acc[key].fine += r.fine_g ?? 0;
            acc[key].spot += r.spot_eur ?? r.book_eur;
            if (r.id.endsWith('_live')) acc[key].liveOn = true;
            if (r.id.endsWith('_manual')) acc[key].manualOn = true;
        }
        for (const g of next.informative.strana_skladista.groups) {
            if (g.key !== 'bugarska' && g.key !== 'slovenija') continue;
            const a = acc[g.key];
            g.book_eur = round2(a.book);
            g.fine_g = round4(a.fine);
            g.spot_eur = round2(a.spot);
            g.source = a.manualOn && !a.liveOn ? 'manual' : a.liveOn && !a.manualOn ? 'live' : a.liveOn || a.manualOn ? 'both' : 'none';
        }
        next.informative.strana_skladista.book_eur = round2(next.informative.strana_skladista.groups.reduce((s, g) => s + g.book_eur, 0));
        next.informative.strana_skladista.fine_g = round4(next.informative.strana_skladista.groups.reduce((s, g) => s + g.fine_g, 0));
        next.informative.strana_skladista.spot_eur = round2(next.informative.strana_skladista.groups.reduce((s, g) => s + g.spot_eur, 0));
    }
    return next;
}

export function GoldCoverageModule({
    token,
    locale = 'hr',
    isOwner = false,
    onBack,
}: {
    token: string;
    locale?: Locale;
    isOwner?: boolean;
    onBack?: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [data, setData] = useState<CoverageData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Active metric view: 'all' (consolidated side-by-side) | 'book' | 'fine' | 'spot'
    const [viewMode, setViewMode] = useState<'all' | 'book' | 'fine' | 'spot'>('all');

    // Active drilldown tab
    const [activeDrilldown, setActiveDrilldown] = useState<number | null>(null);

    // Manual inputs modal
    const [showManualModal, setShowManualModal] = useState(false);
    const [manualInputs, setManualInputs] = useState<ManualInput[]>([]);
    const [manualValues, setManualValues] = useState<Record<string, string>>({});
    const [savingManual, setSavingManual] = useState(false);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    // N23 (zahtjev 1, mail UID 556, 11.09.2026): rep-ovi veleprodaje (bi_gold_wholesale_writeoffs).
    const [showWriteoffForm, setShowWriteoffForm] = useState(false);
    const [writeoffDraft, setWriteoffDraft] = useState({ item_code: '', item_name: '', qty: '', fine_weight_g: '', reason: '', note: '' });
    const [savingWriteoff, setSavingWriteoff] = useState(false);
    const [deletingWriteoffId, setDeletingWriteoffId] = useState<number | null>(null);
    // N23 (zahtjev 4): dnevna rucna potvrda robe kod dobavljaca.
    const [verifyingSuppliers, setVerifyingSuppliers] = useState(false);
    const [showVisibility, setShowVisibility] = useState(false);
    const [visibilityUsers, setVisibilityUsers] = useState<Array<{ id: number; name: string; email: string; role: string; visible: boolean; editable: boolean; locked: boolean }>>([]);
    const [savingVisibility, setSavingVisibility] = useState(false);
    const [accessSearch, setAccessSearch] = useState('');
    const [moduleTab, setModuleTab] = useState<'coverage' | 'retail'>('coverage');
    const [recipientUsers, setRecipientUsers] = useState<Array<{ id: number; name: string | null; email: string | null }>>([]);
    const [recipientIds, setRecipientIds] = useState<Set<number>>(new Set());
    const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
    const [recipientSearch, setRecipientSearch] = useState('');
    const [recipientEmailInput, setRecipientEmailInput] = useState('');
    // Lokalni datum (Zagreb), ne UTC: izmedju 00 i 02 h UTC je jos jucer.
    const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Zagreb' });
    const monthStart = `${today.slice(0, 7)}-01`;
    const [dateFrom, setDateFrom] = useState(monthStart);
    const [dateTo, setDateTo] = useState(today);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
            const res = await api<CoverageData>(`/gold-coverage/overview?${qs.toString()}`, token);
            setData(res);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API Error');
        } finally {
            setLoading(false);
        }
    };

    const loadManualInputs = async () => {
        try {
            const res = await api<{ inputs: ManualInput[] }>('/gold-coverage/manual-inputs', token);
            setManualInputs(res.inputs);
            const vals: Record<string, string> = {};
            res.inputs.forEach((i) => {
                vals[i.key] = String(i.value ?? '');
            });
            setManualValues(vals);
            setShowManualModal(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Manual inputs error');
        }
    };

    const saveManualInputs = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingManual(true);
        try {
            const values: Record<string, any> = {};
            manualInputs.forEach((i) => {
                const raw = manualValues[i.key];
                if (i.unit === 'tekst') {
                    values[i.key] = raw;
                } else {
                    const parsed = parseFloat(raw.replace(',', '.'));
                    values[i.key] = isNaN(parsed) ? 0 : parsed;
                }
            });
            // mutate baca gresku za 403/422, pa modal ostaje otvoren s porukom.
            await mutate('/gold-coverage/manual-inputs', token, 'POST', { values });
            setShowManualModal(false);
            await loadData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save error');
        } finally {
            setSavingManual(false);
        }
    };

    const loadVisibility = async () => {
        const [res, notifications] = await Promise.all([
            api<{ users: typeof visibilityUsers }>('/gold-coverage/visibility', token),
            api<{ items: Array<{ key: string; user_ids: number[]; emails: string[] }>; users: typeof recipientUsers }>('/admin/notification-recipients', token),
        ]);
        setVisibilityUsers(res.users);
        setAccessSearch('');
        setRecipientUsers(notifications.users ?? []);
        const retail = notifications.items.find((item) => item.key === 'gold_coverage_retail_daily');
        setRecipientIds(new Set(retail?.user_ids ?? []));
        setRecipientEmails(retail?.emails ?? []);
        setRecipientSearch('');
        setRecipientEmailInput('');
        setShowVisibility(true);
    };

    const saveVisibility = async () => {
        setSavingVisibility(true);
        try {
            const res = await mutate<{ users: typeof visibilityUsers }>('/gold-coverage/visibility', token, 'PUT', {
                view_user_ids: visibilityUsers.filter((u) => u.visible).map((u) => u.id),
                edit_user_ids: visibilityUsers.filter((u) => u.editable).map((u) => u.id),
            });
            await mutate('/admin/notification-recipients', token, 'PUT', {
                notification_key: 'gold_coverage_retail_daily', user_ids: Array.from(recipientIds), emails: recipientEmails,
            });
            setVisibilityUsers(res.users);
            setShowVisibility(false);
        } finally {
            setSavingVisibility(false);
        }
    };

    const toggleInclude = async (id: string, included: boolean) => {
        if (togglingId) return;
        const previous = data;
        if (previous) {
            setData(applyInclude(previous, id, included));
        }
        setTogglingId(id);
        try {
            await mutate('/gold-coverage/item4-include', token, 'POST', { id, included });
        } catch (err) {
            if (previous) setData(previous);
            setError(err instanceof Error ? err.message : 'Include toggle error');
        } finally {
            setTogglingId(null);
        }
    };

    // N23 (zahtjev 1): dodaj rep veleprodaje - vrijednuje se iskljucivo po spot cijeni.
    const saveWriteoff = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingWriteoff(true);
        try {
            const qty = parseFloat(writeoffDraft.qty.replace(',', '.'));
            const fine = writeoffDraft.fine_weight_g.trim() === '' ? null : parseFloat(writeoffDraft.fine_weight_g.replace(',', '.'));
            await mutate('/gold-coverage/wholesale-writeoffs', token, 'POST', {
                item_code: writeoffDraft.item_code.trim() || null,
                item_name: writeoffDraft.item_name.trim(),
                qty: isNaN(qty) ? 0 : qty,
                fine_weight_g: fine !== null && isNaN(fine) ? null : fine,
                reason: writeoffDraft.reason.trim(),
                note: writeoffDraft.note.trim() || null,
            });
            setWriteoffDraft({ item_code: '', item_name: '', qty: '', fine_weight_g: '', reason: '', note: '' });
            setShowWriteoffForm(false);
            await loadData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save error');
        } finally {
            setSavingWriteoff(false);
        }
    };

    const deleteWriteoff = async (id: number) => {
        if (!confirm(t('Obrisati rep?', 'Delete write-off?'))) return;
        setDeletingWriteoffId(id);
        try {
            await mutate(`/gold-coverage/wholesale-writeoffs/${id}`, token, 'DELETE');
            await loadData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Delete error');
        } finally {
            setDeletingWriteoffId(null);
        }
    };

    // N23 (zahtjev 4): dnevna rucna potvrda "Roba kod dobavljaca".
    const verifySuppliers = async () => {
        setVerifyingSuppliers(true);
        try {
            await mutate('/gold-cm-planner/manual-supplier-orders/verify', token, 'POST');
            await loadData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Verify error');
        } finally {
            setVerifyingSuppliers(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [token]);

    const formatFine = (grams: number) => {
        if (Math.abs(grams) >= 1000) {
            return `${formatNumber(grams / 1000, locale)} kg`;
        }
        return `${formatNumber(grams, locale)} g`;
    };

    const renderIncludeRows = (rows: IncludeRow[] | undefined) => {
        if (!rows || rows.length === 0) {
            return null;
        }
        return (
            <div style={{ marginTop: '0.6rem', fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                {rows.map((row) => (
                    <div
                        key={row.id}
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 8,
                            minWidth: 0,
                            opacity: row.included_in_sum ? 1 : 0.5,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={row.included_in_sum}
                            disabled={!data?.permissions.can_edit || togglingId !== null}
                            onChange={(e) => void toggleInclude(row.id, e.target.checked)}
                            aria-label={row.label}
                            style={{ flexShrink: 0, marginTop: 3 }}
                        />
                        <div style={{ flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>
                            {row.label}
                            <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{row.note}</div>
                        </div>
                        <div style={{ flex: '0 0 auto', fontFamily: 'monospace', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {formatMoney(row.book_eur, locale)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderWarehouseGroups = (groups: WarehouseGroup[] | undefined, emptyLabel: string) => {
        if (!groups || groups.length === 0) {
            return <p style={{ margin: '0.4rem 0 0', fontSize: '0.78rem', color: '#64748b' }}>{emptyLabel}</p>;
        }
        return (
            <div style={{ marginTop: '0.5rem' }}>
                {groups.map((g) => (
                    <div key={g.key} style={{ marginBottom: '0.35rem', fontSize: '0.78rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', minWidth: 0 }}>
                            <span style={{ fontWeight: 600, overflow: 'hidden', wordBreak: 'break-word' }}>
                                {g.label}
                                {g.source === 'manual' ? (
                                    <span style={{ marginLeft: 6, fontWeight: 400, color: '#64748b' }}>{t('ručno', 'manual')}</span>
                                ) : g.source === 'both' ? (
                                    <span style={{ marginLeft: 6, fontWeight: 400, color: '#64748b' }}>{t('živo + ručno', 'live + manual')}</span>
                                ) : null}
                            </span>
                            <span style={{ fontFamily: 'monospace', flexShrink: 0 }}>{formatMoney(g.book_eur, locale)}</span>
                        </div>
                        {g.source === 'manual' && typeof g.live_book_eur === 'number' ? (
                            <div style={{ color: '#94a3b8', paddingLeft: '0.5rem' }}>
                                {t('živo', 'live')}: {formatMoney(g.live_book_eur, locale)}
                                {typeof g.live_fine_g === 'number' ? ` · ${formatFine(g.live_fine_g)}` : ''}
                            </div>
                        ) : null}
                        {g.warehouses?.length === 0 ? (
                            <div style={{ color: '#94a3b8', paddingLeft: '0.5rem' }}>{t('nema stanja', 'no stock')}</div>
                        ) : (
                            g.warehouses?.map((w) => (
                                <div key={w.warehouse_code} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', paddingLeft: '0.5rem', color: '#64748b', minWidth: 0 }}>
                                    <span style={{ overflow: 'hidden', wordBreak: 'break-word' }}>{w.warehouse_code} {w.warehouse_name}</span>
                                    <span style={{ fontFamily: 'monospace', flexShrink: 0 }}>{formatFine(w.fine_g)}</span>
                                </div>
                            ))
                        )}
                    </div>
                ))}
            </div>
        );
    };

    if (loading && !data) {
        return (
            <div className="card admin-block" style={{ padding: '3rem', textAlign: 'center' }}>
                <BrandSpinner />
                <p className="muted" style={{ marginTop: '1rem' }}>
                    {t('Učitavanje izračuna pokrivenosti zlatom...', 'Loading gold coverage calculation...')}
                </p>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="card admin-block" style={{ padding: '2rem' }}>
                <p className="error">{error}</p>
                <button type="button" className="btn btn-secondary" onClick={loadData}>
                    {t('Pokušaj ponovno', 'Retry')}
                </button>
            </div>
        );
    }

    if (!data) return null;

    const { spot, items, coverage, informative } = data;
    const isCoveragePositive = coverage.spot_eur >= 0;
    const subtotal1234 = {
        book: coverage.total_assets_book_eur + items.item_4_dobavljaci.book_eur,
        fine: coverage.total_assets_fine_g + items.item_4_dobavljaci.fine_g,
        spot: coverage.total_assets_spot_eur + items.item_4_dobavljaci.spot_eur,
    };
    const total1234minus5 = {
        book: subtotal1234.book - items.item_5_neisporucene.book_eur,
        fine: subtotal1234.fine - items.item_5_neisporucene.fine_g,
        spot: subtotal1234.spot - items.item_5_neisporucene.spot_eur,
    };

    return (
        <section className="card admin-block gold-coverage-module">
            {/* Header / Breadcrumb */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    {onBack ? <Breadcrumb items={[{ label: t('40 · Skladišno poslovanje', '40 · Warehouse'), onClick: onBack }, { label: '40.6 · ' + t('Pokrivenost zlatom', 'Gold coverage') }]} /> : null}
                    <div className="bi-module-hero" style={{ marginTop: onBack ? '0.5rem' : 0 }}>
                        <div className="bi-module-hero-icon">⚖️</div>
                        <div className="bi-module-hero-text">
                            <h1>{t('40.6 · Pokrivenost zlatom', '40.6 · Gold coverage')}</h1>
                            <p className="muted">
                                {t(
                                    'Tri paralelna prikaza (knjigovodstveno EUR, fine XAU, spot EUR) za stavke 1–6b s formulom (1+2+3+6a+6b)−5, uz stavku 4 kao zaseban podzbroj.',
                                    'Three parallel views (book EUR, fine XAU, spot EUR) for items 1–6b with formula (1+2+3+6a+6b)−5, item 4 shown as a separate subtotal.'
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {isOwner ? <button type="button" className="btn btn-secondary" onClick={() => void loadVisibility()}>{t('Postavke', 'Settings')}</button> : null}
                    {data.permissions.can_edit ? <button type="button" className="btn btn-secondary" onClick={loadManualInputs} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                        {t('Ručni unosi', 'Manual inputs')}
                    </button> : null}
                    <button type="button" className="btn btn-secondary" onClick={loadData} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                        🔄 {t('Osvježi', 'Refresh')}
                    </button>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: '1rem', borderBottom: '1px solid #cbd5e1' }}>
                <button type="button" className="btn" onClick={() => setModuleTab('coverage')} style={{ borderBottom: moduleTab === 'coverage' ? '3px solid #475569' : '3px solid transparent' }}>{t('Pokrivenost', 'Coverage')}</button>
                <button type="button" className="btn" onClick={() => setModuleTab('retail')} style={{ borderBottom: moduleTab === 'retail' ? '3px solid #475569' : '3px solid transparent' }}>{t('Maloprodaja dnevno', 'Daily retail')}</button>
            </div>

            <div style={{ display: moduleTab === 'coverage' ? 'contents' : 'none' }}>

            {/* Spot Quote & Snapshot Banner */}
            <div
                style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '0.75rem 1.25rem',
                    margin: '1.25rem 0',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                    fontSize: '0.875rem',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                    <div>
                        <span className="muted">{t('StoneX Spot:', 'StoneX Spot:')} </span>
                        <strong>{formatMoney(spot.price_eur_per_g, locale)}/g</strong>
                        <span className="muted" style={{ marginLeft: '0.35rem' }}>
                            ({formatMoney(spot.price_eur_per_troy_oz, locale)}/oz)
                        </span>
                    </div>
                    {spot.spot_date ? (
                        <div>
                            <span className="muted">{t('Datum spota:', 'Spot date:')} </span>
                            <span>{formatDate(spot.spot_date, locale)}</span>
                        </div>
                    ) : null}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                    {data.snapshots.stock_batch_id ? (
                        <div>
                            <span className="muted">{t('Zalihe batch:', 'Stock batch:')} </span>
                            <code>#{data.snapshots.stock_batch_id}</code>
                        </div>
                    ) : null}
                    {data.snapshots.open_orders_snapshot_at ? (
                        <div>
                            <span className="muted">{t('Narudžbe snapshot:', 'Orders snapshot:')} </span>
                            <code>{formatDateTime(data.snapshots.open_orders_snapshot_at, locale)}</code>
                        </div>
                    ) : null}
                </div>
            </div>

            {data.unknown_warehouses?.length ? (
                <p className="card-alert card-alert-danger error" style={{ padding: '0.5rem 0 0.5rem 1rem' }}>
                    {locale === 'en'
                        ? 'Warehouses in the stock snapshot without a row in admin 128, counted as branches: '
                        : 'Skladista u snimci zalihe bez retka u adminu 128, racunaju se kao poslovnica: '}
                    {data.unknown_warehouses.map((w) => `${w.code} ${w.name}`).join(', ')}
                </p>
            ) : null}

            {/* Top KPI Cards */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1.5rem',
                }}
            >
                {/* Spot Coverage Card */}
                <div
                    className="card"
                    style={{
                        padding: '1.25rem',
                        borderLeft: `4px solid ${isCoveragePositive ? '#16a34a' : '#dc2626'}`,
                        background: isCoveragePositive ? '#f0fdf4' : '#fef2f2',
                    }}
                >
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        X - {t('RASPOLOŽIVO MINUS NEISPORUČENO (SPOT EUR)', 'AVAILABLE MINUS UNDELIVERED (SPOT EUR)')}
                    </p>
                    <h2
                        style={{
                            margin: '0.4rem 0 0.2rem',
                            color: isCoveragePositive ? '#15803d' : '#b91c1c',
                            fontSize: '1.75rem',
                        }}
                    >
                        {formatMoney(coverage.spot_eur, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {t('Bez robe kod dobavljača', 'Supplier goods excluded')}
                    </p>
                </div>

                {/* Fine XAU Coverage Card */}
                <div
                    className="card"
                    style={{
                        padding: '1.25rem',
                        borderLeft: `4px solid ${coverage.fine_g >= 0 ? '#ca8a04' : '#dc2626'}`,
                        background: coverage.fine_g >= 0 ? '#fefce8' : '#fef2f2',
                    }}
                >
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        X - {t('FINE XAU', 'FINE XAU')}
                    </p>
                    <h2
                        style={{
                            margin: '0.4rem 0 0.2rem',
                            color: coverage.fine_g >= 0 ? '#a16207' : '#b91c1c',
                            fontSize: '1.75rem',
                        }}
                    >
                        {formatFine(coverage.fine_g)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {formatNumber(coverage.fine_g, locale)} g fine
                    </p>
                </div>

                {/* Book EUR Coverage Card */}
                <div
                    className="card"
                    style={{
                        padding: '1.25rem',
                        borderLeft: `4px solid ${coverage.book_eur >= 0 ? '#2563eb' : '#dc2626'}`,
                        background: coverage.book_eur >= 0 ? '#eff6ff' : '#fef2f2',
                    }}
                >
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        X - {t('KNJIGOVODSTVENO', 'BOOK VALUE')}
                    </p>
                    <h2
                        style={{
                            margin: '0.4rem 0 0.2rem',
                            color: coverage.book_eur >= 0 ? '#1d4ed8' : '#b91c1c',
                            fontSize: '1.75rem',
                        }}
                    >
                        {formatMoney(coverage.book_eur, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {t('Knjig. pokriće:', 'Book ratio:')} <strong>{formatNumber(coverage.ratio_book_pct, locale)}%</strong>
                    </p>
                </div>

                {/* Imovina vs Obveze Summary */}
                <div
                    className="card"
                    style={{
                        padding: '1.25rem',
                        borderLeft: '4px solid #64748b',
                        background: '#f8fafc',
                    }}
                >
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        {t('RASPOLOŽIVO (1+2+3+6a+6b) VS NEISPORUČENO (5)', 'AVAILABLE (1+2+3+6a+6b) VS UNDELIVERED (5)')}
                    </p>
                    <p style={{ margin: '0.4rem 0 0.2rem', fontSize: '1.1rem', fontWeight: 600 }}>
                        {formatMoney(coverage.total_assets_spot_eur, locale)}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                        {t('Neisporučeno:', 'Undelivered:')} {formatMoney(items.item_5_neisporucene.spot_eur, locale)} ({items.item_5_neisporucene.order_count} {t('nar.', 'ord.')})
                    </p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="card" style={{ padding: '1rem' }}>
                    <p className="eyebrow">{t('ROBA KOD DOBAVLJAČA - IZVAN X', 'SUPPLIER GOODS - OUTSIDE X')}</p>
                    <h3>{formatMoney(coverage.supplier_goods.money_delta_eur, locale)}</h3>
                    <p className="muted">{t('Novac +/-', 'Money +/-')}: {formatMoney(coverage.supplier_goods.money_delta_eur, locale)}</p>
                    <p className="muted">{t('Fiksirana roba', 'Fixed goods')}: {formatFine(coverage.supplier_goods.fixed_fine_g)}</p>
                </div>
                <div className="card" style={{ padding: '1rem' }}>
                    <p className="eyebrow">{t('DANI KAŠNJENJA - ADO EUR', 'DELAY DAYS - ADO EUR')}</p>
                    <h3>{coverage.delay_days.days_eur === null ? 'N/A' : formatNumber(coverage.delay_days.days_eur, locale)}</h3>
                    <p className="muted">X / {t('prosječni dnevni promet', 'average daily turnover')} ({formatMoney(coverage.delay_days.ado_daily_eur, locale)})</p>
                </div>
                <div className="card" style={{ padding: '1rem' }}>
                    <p className="eyebrow">{t('DANI KAŠNJENJA - ADO FINE', 'DELAY DAYS - ADO FINE')}</p>
                    <h3>{coverage.delay_days.days_fine === null ? 'N/A' : formatNumber(coverage.delay_days.days_fine, locale)}</h3>
                    <p className="muted">X / {t('prosječni dnevni promet', 'average daily turnover')} ({formatFine(coverage.delay_days.ado_daily_fine_g)})</p>
                </div>
                <div className="card" style={{ padding: '1rem' }}>
                    <p className="eyebrow">{t('ADO PROMET U RAZDOBLJU', 'ADO TURNOVER IN PERIOD')}</p>
                    <h3>{formatMoney(coverage.delay_days.ado_turnover_eur, locale)}</h3>
                    <p className="muted">{formatFine(coverage.delay_days.ado_turnover_fine_g)} - {coverage.delay_days.period_days} {t('dana', 'days')}</p>
                </div>
            </div>
            </div>

            {moduleTab === 'retail' ? <div className="card gold-retail-card">
                <div className="gold-retail-heading">
                    <div>
                        <p className="eyebrow">{t('Pregled po poslovnicama', 'Overview by branch')}</p>
                        <h3>{t('Maloprodaja dnevno', 'Daily retail')}</h3>
                    </div>
                    <p className="gold-retail-scope">{t('Opseg', 'Scope')}: {data.retail_daily.scope} - {data.retail_daily.date_from} / {data.retail_daily.date_to}</p>
                </div>
                <div className="gold-retail-filters">
                    <label>{t('Promet od', 'Turnover from')}<input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></label>
                    <label>{t('Promet do', 'Turnover to')}<input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} /></label>
                    <button type="button" className="btn btn-secondary" onClick={() => void loadData()}>{t('Primijeni razdoblje', 'Apply period')}</button>
                </div>
                <div className="gold-retail-table-wrap"><table className="admin-table gold-retail-table">
                    <thead><tr>
                        <th>{t('Poslovnica', 'Branch')}</th><th>{t('Lager EUR', 'Stock EUR')}</th><th>{t('Na putu EUR', 'In transit EUR')}</th>
                        <th>{t('Promet EUR', 'Turnover EUR')}</th><th>{t('Promet fine', 'Fine turnover')}</th><th>{t('Prosjek/dan', 'Average/day')}</th><th>{t('Brzina pražnjenja', 'Depletion speed')}</th>
                    </tr></thead>
                    <tbody>
                        {data.retail_daily.rows?.map((r) => <tr key={r.warehouse_code}>
                            <td>{r.warehouse_code} {r.warehouse_name}</td><td>{formatMoney(r.stock_eur, locale)}</td><td>{formatMoney(r.in_transit_eur, locale)}</td>
                            <td>{formatMoney(r.sales_eur, locale)}</td><td>{formatFine(r.sales_fine_g)}</td><td>{formatMoney(r.avg_daily_sales_eur, locale)}</td>
                            <td>{r.depletion_days === null ? 'N/A' : `${formatNumber(r.depletion_days, locale)} ${t('dana', 'days')}`}</td>
                        </tr>)}
                    </tbody>
                    <tfoot><tr><th>{t('Zbirno', 'Total')}</th><th>{formatMoney(data.retail_daily.totals.stock_eur, locale)}</th><th>{formatMoney(data.retail_daily.totals.in_transit_eur, locale)}</th><th>{formatMoney(data.retail_daily.totals.sales_eur, locale)}</th><th>{formatFine(data.retail_daily.totals.sales_fine_g)}</th><th></th><th></th></tr></tfoot>
                </table></div>
            </div> : null}

            <div style={{ display: moduleTab === 'coverage' ? 'contents' : 'none' }}>

            {/* View Mode Switcher */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>{t('Glavna tablica pokrivenosti (1–5)', 'Main coverage table (1–5)')}</h3>
                <div style={{ display: 'inline-flex', background: '#e2e8f0', borderRadius: '6px', padding: '2px' }}>
                    <button
                        type="button"
                        className="btn"
                        style={{
                            padding: '4px 12px',
                            fontSize: '0.8rem',
                            borderRadius: '4px',
                            background: viewMode === 'all' ? '#ffffff' : 'transparent',
                            boxShadow: viewMode === 'all' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                            color: viewMode === 'all' ? '#0f172a' : '#64748b',
                            fontWeight: viewMode === 'all' ? 600 : 400,
                        }}
                        onClick={() => setViewMode('all')}
                    >
                        {t('Konsolidirano (Sve)', 'Consolidated (All)')}
                    </button>
                    <button
                        type="button"
                        className="btn"
                        style={{
                            padding: '4px 12px',
                            fontSize: '0.8rem',
                            borderRadius: '4px',
                            background: viewMode === 'spot' ? '#ffffff' : 'transparent',
                            boxShadow: viewMode === 'spot' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                            color: viewMode === 'spot' ? '#0f172a' : '#64748b',
                            fontWeight: viewMode === 'spot' ? 600 : 400,
                        }}
                        onClick={() => setViewMode('spot')}
                    >
                        {t('Spot (€)', 'Spot (€)')}
                    </button>
                    <button
                        type="button"
                        className="btn"
                        style={{
                            padding: '4px 12px',
                            fontSize: '0.8rem',
                            borderRadius: '4px',
                            background: viewMode === 'fine' ? '#ffffff' : 'transparent',
                            boxShadow: viewMode === 'fine' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                            color: viewMode === 'fine' ? '#0f172a' : '#64748b',
                            fontWeight: viewMode === 'fine' ? 600 : 400,
                        }}
                        onClick={() => setViewMode('fine')}
                    >
                        {t('Fine XAU (g)', 'Fine XAU (g)')}
                    </button>
                    <button
                        type="button"
                        className="btn"
                        style={{
                            padding: '4px 12px',
                            fontSize: '0.8rem',
                            borderRadius: '4px',
                            background: viewMode === 'book' ? '#ffffff' : 'transparent',
                            boxShadow: viewMode === 'book' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                            color: viewMode === 'book' ? '#0f172a' : '#64748b',
                            fontWeight: viewMode === 'book' ? 600 : 400,
                        }}
                        onClick={() => setViewMode('book')}
                    >
                        {t('Knjigovodstveno (€)', 'Book value (€)')}
                    </button>
                </div>
            </div>

            {/* Main Coverage Table */}
            <div style={{ overflowX: 'auto', marginBottom: '2rem' }}>
                <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1', textAlign: 'left' }}>
                            <th style={{ padding: '0.75rem 1rem', width: '45px' }}>#</th>
                            <th style={{ padding: '0.75rem 1rem' }}>{t('Stavka', 'Item')}</th>
                            {viewMode === 'all' || viewMode === 'book' ? (
                                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                                    {t('Knjigovodstveno (EUR)', 'Book value (EUR)')}
                                </th>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'fine' ? (
                                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                                    {t('Fine XAU (g)', 'Fine XAU (g)')}
                                </th>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'spot' ? (
                                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                                    {t('Spot (EUR)', 'Spot (EUR)')}
                                </th>
                            ) : null}
                            <th style={{ padding: '0.75rem 1rem', textAlign: 'center', width: '120px' }}>{t('Detalji', 'Details')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Item 1: Veleprodaja */}
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>1</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                                <strong>{t('Veleprodaja', 'Wholesale')}</strong>
                                <span className="muted" style={{ display: 'block', fontSize: '0.8rem' }}>
                                    {t('Centralno skladište 064 — slobodna roba na zalihi', 'Central warehouse 064 — free stock')}
                                    {items.item_1_veleprodaja.qty > 0 ? ` (${formatNumber(items.item_1_veleprodaja.qty, locale)} kom)` : ''}
                                </span>
                            </td>
                            {viewMode === 'all' || viewMode === 'book' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatMoney(items.item_1_veleprodaja.book_eur, locale)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'fine' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatFine(items.item_1_veleprodaja.fine_g)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'spot' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 600 }}>
                                    {formatMoney(items.item_1_veleprodaja.spot_eur, locale)}
                                </td>
                            ) : null}
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setActiveDrilldown(activeDrilldown === 1 ? null : 1)}
                                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                >
                                    {activeDrilldown === 1 ? t('Zatvori', 'Close') : t('Prikaži', 'View')}
                                </button>
                            </td>
                        </tr>

                        {/* Item 2: Poslovnice */}
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>2</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                                <strong>{t('Poslovnice ("smješteno")', 'Branches ("reserved")')}</strong>
                                <span className="muted" style={{ display: 'block', fontSize: '0.8rem' }}>
                                    {t('Od 11.09.2026: samo zaliha rezervirana kroz "smješteno". Ostatak je stavka 6a.', 'From 11.09.2026: only stock reserved via "received". Remainder is item 6a.')}
                                    {items.item_2_poslovnice.qty > 0 ? ` (${formatNumber(items.item_2_poslovnice.qty, locale)} kom)` : ''}
                                </span>
                            </td>
                            {viewMode === 'all' || viewMode === 'book' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatMoney(items.item_2_poslovnice.book_eur, locale)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'fine' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatFine(items.item_2_poslovnice.fine_g)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'spot' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 600 }}>
                                    {formatMoney(items.item_2_poslovnice.spot_eur, locale)}
                                </td>
                            ) : null}
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setActiveDrilldown(activeDrilldown === 2 ? null : 2)}
                                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                >
                                    {activeDrilldown === 2 ? t('Zatvori', 'Close') : t('Prikaži', 'View')}
                                </button>
                            </td>
                        </tr>

                        {/* Item 3: Na putu */}
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>3</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                                <strong>{t('Na putu', 'In transit')}</strong>
                                <span className="muted" style={{ display: 'block', fontSize: '0.8rem' }}>
                                    {t('Roba u tranzitu / međuskladišnice u dolasku', 'Goods in transit / incoming transfers')}
                                    {items.item_3_na_putu.qty > 0 ? ` (${formatNumber(items.item_3_na_putu.qty, locale)} kom)` : ''}
                                </span>
                            </td>
                            {viewMode === 'all' || viewMode === 'book' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatMoney(items.item_3_na_putu.book_eur, locale)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'fine' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatFine(items.item_3_na_putu.fine_g)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'spot' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 600 }}>
                                    {formatMoney(items.item_3_na_putu.spot_eur, locale)}
                                </td>
                            ) : null}
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setActiveDrilldown(activeDrilldown === 3 ? null : 3)}
                                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                >
                                    {activeDrilldown === 3 ? t('Zatvori', 'Close') : t('Prikaži', 'View')}
                                </button>
                            </td>
                        </tr>

                        {/* Item 6a: Lager (poslovnice bez rezervacije) */}
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>6a</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                                <strong>{t('Lager (poslovnice, bez rezervacije)', 'Lager (branches, unreserved)')}</strong>
                                <span className="muted" style={{ display: 'block', fontSize: '0.8rem' }}>
                                    {t('Od 11.09.2026 (zahtjev 6a) ulazi u formulu. Isključivo po spot cijeni.', 'From 11.09.2026 (req. 6a) enters the formula. Spot price only.')}
                                    {items.item_6a_lager.qty > 0 ? ` (${formatNumber(items.item_6a_lager.qty, locale)} kom)` : ''}
                                </span>
                            </td>
                            {viewMode === 'all' || viewMode === 'book' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatMoney(items.item_6a_lager.book_eur, locale)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'fine' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatFine(items.item_6a_lager.fine_g)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'spot' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 600 }}>
                                    {formatMoney(items.item_6a_lager.spot_eur, locale)}
                                </td>
                            ) : null}
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setActiveDrilldown(activeDrilldown === 6.1 ? null : 6.1)}
                                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                >
                                    {activeDrilldown === 6.1 ? t('Zatvori', 'Close') : t('Prikaži', 'View')}
                                </button>
                            </td>
                        </tr>

                        {/* Item 6b: Akcijsko skladište */}
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>6b</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                                <strong>{t('Akcijsko skladište', 'Promo warehouse')}</strong>
                                <span className="muted" style={{ display: 'block', fontSize: '0.8rem' }}>
                                    {t('m.40.3 (dok ne stigne Faros). Isključivo po spot cijeni.', 'm.40.3 (until Faros arrives). Spot price only.')}
                                    {items.item_6b_akcijsko.qty > 0 ? ` (${formatNumber(items.item_6b_akcijsko.qty, locale)} kom)` : ''}
                                </span>
                            </td>
                            {viewMode === 'all' || viewMode === 'book' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatMoney(items.item_6b_akcijsko.book_eur, locale)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'fine' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatFine(items.item_6b_akcijsko.fine_g)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'spot' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 600 }}>
                                    {formatMoney(items.item_6b_akcijsko.spot_eur, locale)}
                                </td>
                            ) : null}
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setActiveDrilldown(activeDrilldown === 6.2 ? null : 6.2)}
                                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                >
                                    {activeDrilldown === 6.2 ? t('Zatvori', 'Close') : t('Prikaži', 'View')}
                                </button>
                            </td>
                        </tr>

                        {/* Item 4: Roba kod dobavljača */}
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>4</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                                <strong>{t('Roba kod dobavljača (ručno + potvrđeni 40.4)', 'Goods at suppliers (manual + confirmed 40.4)')}</strong>
                                <span className="muted" style={{ display: 'block', fontSize: '0.8rem' }}>
                                    {t('Ručni unos i potvrđene neisporučene L3 narudžbe iz Plan & generate, uparene s otvorenim predujmovima. Kvačica = ulazi u zbroj.', 'Manual input and confirmed undelivered 40.4 L3 orders matched to open advances. Checkbox = counts in the sum.')}
                                </span>
                                {items.item_4_dobavljaci.verification_warning ? (
                                    <span style={{ display: 'inline-block', marginTop: '4px', padding: '2px 8px', borderRadius: '4px', background: '#fef3c7', color: '#92400e', fontSize: '0.75rem', fontWeight: 600 }}>
                                        {t('⚠ Nije potvrđeno danas (zahtjev 4)', '⚠ Not verified today (req. 4)')}
                                    </span>
                                ) : items.item_4_dobavljaci.last_verified_at ? (
                                    <span className="muted" style={{ display: 'block', fontSize: '0.75rem' }}>
                                        {t('Potvrđeno:', 'Verified:')} {formatDateTime(items.item_4_dobavljaci.last_verified_at, locale)}
                                    </span>
                                ) : null}
                            </td>
                            {viewMode === 'all' || viewMode === 'book' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatMoney(items.item_4_dobavljaci.book_eur, locale)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'fine' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                    {formatFine(items.item_4_dobavljaci.fine_g)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'spot' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 600 }}>
                                    {formatMoney(items.item_4_dobavljaci.spot_eur, locale)}
                                </td>
                            ) : null}
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setActiveDrilldown(activeDrilldown === 4 ? null : 4)}
                                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                >
                                    {activeDrilldown === 4 ? t('Zatvori', 'Close') : t('Prikaži', 'View')}
                                </button>
                            </td>
                        </tr>

                        {/* Item 5: Neisporučene narudžbe */}
                        <tr style={{ borderBottom: '2px solid #cbd5e1', background: '#fef2f2' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#b91c1c' }}>5</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                                <strong style={{ color: '#b91c1c' }}>{t('Neisporučene narudžbe (−)', 'Undelivered orders (−)')}</strong>
                                <span className="muted" style={{ display: 'block', fontSize: '0.8rem' }}>
                                    {t('Obveza isporuke klijentima po otvorenim predujmovima / narudžbama', 'Delivery obligations to clients from open orders')}
                                    {items.item_5_neisporucene.order_count > 0 ? ` (${items.item_5_neisporucene.order_count} ${t('narudžbi', 'orders')})` : ''}
                                </span>
                            </td>
                            {viewMode === 'all' || viewMode === 'book' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', color: '#b91c1c' }}>
                                    −{formatMoney(items.item_5_neisporucene.book_eur, locale)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'fine' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', color: '#b91c1c' }}>
                                    −{formatFine(items.item_5_neisporucene.fine_g)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'spot' ? (
                                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 600, color: '#b91c1c' }}>
                                    −{formatMoney(items.item_5_neisporucene.spot_eur, locale)}
                                </td>
                            ) : null}
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setActiveDrilldown(activeDrilldown === 5 ? null : 5)}
                                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                >
                                    {activeDrilldown === 5 ? t('Zatvori', 'Close') : t('Prikaži', 'View')}
                                </button>
                            </td>
                        </tr>

                        <tr style={{ background: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                            <td style={{ padding: '0.8rem 1rem', fontWeight: 700 }}>Σ</td>
                            <td style={{ padding: '0.8rem 1rem', fontWeight: 700 }}>{t('Raspoloživo 1+2+3+6a+6b', 'Available 1+2+3+6a+6b')}</td>
                            {viewMode === 'all' || viewMode === 'book' ? <td style={{ padding: '0.8rem 1rem', textAlign: 'right', fontWeight: 700 }}>{formatMoney(coverage.total_assets_book_eur, locale)}</td> : null}
                            {viewMode === 'all' || viewMode === 'fine' ? <td style={{ padding: '0.8rem 1rem', textAlign: 'right', fontWeight: 700 }}>{formatFine(coverage.total_assets_fine_g)}</td> : null}
                            {viewMode === 'all' || viewMode === 'spot' ? <td style={{ padding: '0.8rem 1rem', textAlign: 'right', fontWeight: 700 }}>{formatMoney(coverage.total_assets_spot_eur, locale)}</td> : null}
                            <td></td>
                        </tr>
                        <tr style={{ background: '#f1f5f9' }}>
                            <td style={{ padding: '0.8rem 1rem', fontWeight: 700 }}>Σ</td>
                            <td style={{ padding: '0.8rem 1rem', fontWeight: 700 }}>{t('Ukupno zlato 1+2+3+6a+6b+4', 'Total gold 1+2+3+6a+6b+4')}</td>
                            {viewMode === 'all' || viewMode === 'book' ? <td style={{ padding: '0.8rem 1rem', textAlign: 'right', fontWeight: 700 }}>{formatMoney(subtotal1234.book, locale)}</td> : null}
                            {viewMode === 'all' || viewMode === 'fine' ? <td style={{ padding: '0.8rem 1rem', textAlign: 'right', fontWeight: 700 }}>{formatFine(subtotal1234.fine)}</td> : null}
                            {viewMode === 'all' || viewMode === 'spot' ? <td style={{ padding: '0.8rem 1rem', textAlign: 'right', fontWeight: 700 }}>{formatMoney(subtotal1234.spot, locale)}</td> : null}
                            <td></td>
                        </tr>

                        {/* X: raspolozivo (1 + 2 + 3) minus neisporuceno (5); stavka 4 je zasebna. */}
                        <tr style={{ background: isCoveragePositive ? '#ecfdf5' : '#fef2f2', borderBottom: '2px solid #94a3b8' }}>
                            <td style={{ padding: '1rem', fontWeight: 700, fontSize: '1.1rem' }}>Σ</td>
                            <td style={{ padding: '1rem' }}>
                                <strong style={{ fontSize: '1.1rem', color: isCoveragePositive ? '#065f46' : '#991b1b' }}>
                                    X = {t('(1+2+3+6a+6b) − 5', '(1+2+3+6a+6b) − 5')}
                                </strong>
                                <span style={{ display: 'block', fontSize: '0.85rem', color: '#475569', marginTop: '2px' }}>
                                    {t('Roba kod dobavljača i lom prikazani su zasebno i ne ulaze u X.', 'Supplier goods and scrap are shown separately and excluded from X.')}
                                </span>
                            </td>
                            {viewMode === 'all' || viewMode === 'book' ? (
                                <td style={{ padding: '1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '1.15rem', fontWeight: 700, color: coverage.book_eur >= 0 ? '#1e40af' : '#991b1b' }}>
                                    {formatMoney(coverage.book_eur, locale)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'fine' ? (
                                <td style={{ padding: '1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '1.15rem', fontWeight: 700, color: coverage.fine_g >= 0 ? '#854d0e' : '#991b1b' }}>
                                    {formatFine(coverage.fine_g)}
                                </td>
                            ) : null}
                            {viewMode === 'all' || viewMode === 'spot' ? (
                                <td style={{ padding: '1rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '1.25rem', fontWeight: 800, color: isCoveragePositive ? '#047857' : '#b91c1c' }}>
                                    {formatMoney(coverage.spot_eur, locale)}
                                </td>
                            ) : null}
                            <td style={{ padding: '1rem', textAlign: 'center' }}>
                                <span
                                    style={{
                                        display: 'inline-block',
                                        padding: '4px 10px',
                                        borderRadius: '999px',
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        background: isCoveragePositive ? '#10b981' : '#ef4444',
                                        color: '#ffffff',
                                    }}
                                >
                                    {formatNumber(coverage.ratio_pct, locale)}%
                                </span>
                            </td>
                        </tr>
                        <tr style={{ background: total1234minus5.spot >= 0 ? '#f0fdf4' : '#fff1f2', borderBottom: '2px solid #64748b' }}>
                            <td style={{ padding: '1rem', fontWeight: 800 }}>Σ</td>
                            <td style={{ padding: '1rem', fontWeight: 800 }}>{t('Ukupno s dobavljačima Y = (1+2+3+6a+6b+4) - 5', 'Total with suppliers Y = (1+2+3+6a+6b+4) - 5')}</td>
                            {viewMode === 'all' || viewMode === 'book' ? <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 800 }}>{formatMoney(total1234minus5.book, locale)}</td> : null}
                            {viewMode === 'all' || viewMode === 'fine' ? <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 800 }}>{formatFine(total1234minus5.fine)}</td> : null}
                            {viewMode === 'all' || viewMode === 'spot' ? <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 800 }}>{formatMoney(total1234minus5.spot, locale)}</td> : null}
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Drilldown details section when an item is selected */}
            {activeDrilldown !== null ? (
                <div style={{ marginBottom: '2.5rem', padding: '1.25rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4 style={{ margin: 0 }}>
                            {activeDrilldown === 1 && t('Razrada 1: Veleprodaja artikli i repovi', 'Breakdown 1: Wholesale items and write-offs')}
                            {activeDrilldown === 2 && t('Razrada 2: Poslovnice po skladištima ("smješteno")', 'Breakdown 2: Branches by warehouse ("reserved")')}
                            {activeDrilldown === 3 && t('Razrada 3: Roba na putu po odredištima', 'Breakdown 3: In transit by destination')}
                            {activeDrilldown === 4 && t('Razrada 4: Roba kod dobavljača (ručno + potvrđeni 40.4)', 'Breakdown 4: Supplier goods (manual + confirmed 40.4)')}
                            {activeDrilldown === 5 && t('Razrada 5: Neisporučene narudžbe status (5a isporuka / 5b pohrana)', 'Breakdown 5: Undelivered orders status (5a delivery / 5b storage)')}
                            {activeDrilldown === 6.1 && t('Razrada 6a: Lager po skladištima', 'Breakdown 6a: Lager by warehouse')}
                            {activeDrilldown === 6.2 && t('Razrada 6b: Akcijsko skladište', 'Breakdown 6b: Promo warehouse')}
                        </h4>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => setActiveDrilldown(null)}>
                            ✕ {t('Zatvori', 'Close')}
                        </button>
                    </div>

                    {/* Drilldown 1: Wholesale */}
                    {activeDrilldown === 1 ? (
                        <div>
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {items.item_1_veleprodaja.breakdown?.length === 0 ? (
                                    <p className="muted">{t('Nema artikala na stanju veleprodaje.', 'No items in wholesale stock.')}</p>
                                ) : (
                                    <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr style={{ background: '#f1f5f9' }}>
                                                <th style={{ padding: '6px 10px' }}>SKU</th>
                                                <th style={{ padding: '6px 10px' }}>{t('Naziv artikla', 'Item name')}</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Količina', 'Qty')}</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Fine XAU (g)', 'Fine XAU (g)')}</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Knjig. (€)', 'Book (€)')}</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Spot (€)', 'Spot (€)')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.item_1_veleprodaja.breakdown?.map((it, idx) => (
                                                <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                    <td style={{ padding: '6px 10px' }}><code>{it.item_code}</code></td>
                                                    <td style={{ padding: '6px 10px' }}>{it.item_name || '—'}</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatNumber(it.qty, locale)}</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(it.fine_g, locale)} g</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(it.book_eur, locale)}</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(it.spot_eur, locale)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* N23 (zahtjev 1): rep-ovi veleprodaje - oduzimaju se od bruto stanja po spot cijeni. */}
                            <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <div>
                                        <strong style={{ fontSize: '0.85rem' }}>{t('Repovi (isknjiženo iz veleprodaje)', 'Write-offs (removed from wholesale)')}</strong>
                                        <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: '0.78rem' }}>
                                            {t('Bruto', 'Gross')}: {formatMoney(items.item_1_veleprodaja.gross_book_eur, locale)} / {formatNumber(items.item_1_veleprodaja.gross_fine_g, locale)} g /{' '}
                                            {formatMoney(items.item_1_veleprodaja.gross_spot_eur, locale)} {t('spot', 'spot')} — {t('repovi', 'write-offs')}:{' '}
                                            {formatNumber(items.item_1_veleprodaja.writeoffs.fine_g, locale)} g / {formatMoney(items.item_1_veleprodaja.writeoffs.spot_eur, locale)}.
                                        </p>
                                    </div>
                                    {data.permissions.can_edit ? (
                                        <button type="button" className="btn btn-secondary" style={{ fontSize: '0.8rem' }} onClick={() => setShowWriteoffForm((v) => !v)}>
                                            {showWriteoffForm ? t('Odustani', 'Cancel') : t('+ Dodaj rep', '+ Add write-off')}
                                        </button>
                                    ) : null}
                                </div>

                                {showWriteoffForm ? (
                                    <form onSubmit={saveWriteoff} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem', marginTop: '0.6rem', background: '#f8fafc', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                        <input type="text" placeholder={t('Šifra (opcionalno)', 'Code (optional)')} value={writeoffDraft.item_code} onChange={(e) => setWriteoffDraft({ ...writeoffDraft, item_code: e.target.value })} />
                                        <input type="text" required placeholder={t('Naziv artikla', 'Item name')} value={writeoffDraft.item_name} onChange={(e) => setWriteoffDraft({ ...writeoffDraft, item_name: e.target.value })} style={{ gridColumn: 'span 2' }} />
                                        <input type="text" required inputMode="decimal" placeholder={t('Količina', 'Qty')} value={writeoffDraft.qty} onChange={(e) => setWriteoffDraft({ ...writeoffDraft, qty: e.target.value })} />
                                        <input type="text" inputMode="decimal" placeholder={t('Fine g (opcionalno)', 'Fine g (optional)')} value={writeoffDraft.fine_weight_g} onChange={(e) => setWriteoffDraft({ ...writeoffDraft, fine_weight_g: e.target.value })} />
                                        <input type="text" required placeholder={t('Razlog', 'Reason')} value={writeoffDraft.reason} onChange={(e) => setWriteoffDraft({ ...writeoffDraft, reason: e.target.value })} />
                                        <input type="text" placeholder={t('Napomena', 'Note')} value={writeoffDraft.note} onChange={(e) => setWriteoffDraft({ ...writeoffDraft, note: e.target.value })} style={{ gridColumn: 'span 2' }} />
                                        <button type="submit" className="btn btn-primary" disabled={savingWriteoff} style={{ fontSize: '0.8rem' }}>
                                            {savingWriteoff ? t('Spremam…', 'Saving…') : t('Spremi', 'Save')}
                                        </button>
                                    </form>
                                ) : null}

                                {items.item_1_veleprodaja.writeoffs.rows?.length > 0 ? (
                                    <table className="admin-table" style={{ width: '100%', fontSize: '0.8rem', marginTop: '0.6rem' }}>
                                        <thead>
                                            <tr style={{ background: '#f1f5f9' }}>
                                                <th style={{ padding: '6px 10px' }}>SKU</th>
                                                <th style={{ padding: '6px 10px' }}>{t('Naziv', 'Name')}</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Kol.', 'Qty')}</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Fine (g)', 'Fine (g)')}</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Spot (€)', 'Spot (€)')}</th>
                                                <th style={{ padding: '6px 10px' }}>{t('Razlog', 'Reason')}</th>
                                                <th style={{ padding: '6px 10px' }}>{t('Unijeto', 'Entered')}</th>
                                                {data.permissions.can_edit ? <th style={{ padding: '6px 10px' }} /> : null}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.item_1_veleprodaja.writeoffs.rows?.map((r) => (
                                                <tr key={r.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                    <td style={{ padding: '6px 10px' }}><code>{r.item_code || '—'}</code></td>
                                                    <td style={{ padding: '6px 10px' }}>{r.item_name || '—'}</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatNumber(r.qty, locale)}</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(r.fine_g, locale)} g</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(r.spot_eur, locale)}</td>
                                                    <td style={{ padding: '6px 10px' }}>{r.reason || '—'}</td>
                                                    <td style={{ padding: '6px 10px', color: '#64748b' }}>{r.entered_at ? formatDateTime(r.entered_at, locale) : '—'}</td>
                                                    {data.permissions.can_edit ? (
                                                        <td style={{ padding: '6px 10px' }}>
                                                            <button type="button" className="btn btn-secondary" style={{ fontSize: '0.75rem' }} disabled={deletingWriteoffId === r.id} onClick={() => void deleteWriteoff(r.id)}>
                                                                {t('Obriši', 'Delete')}
                                                            </button>
                                                        </td>
                                                    ) : null}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p className="muted" style={{ margin: '0.5rem 0 0', fontSize: '0.78rem' }}>{t('Nema evidentiranih repova.', 'No write-offs recorded.')}</p>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {/* Drilldown 2: Branches */}
                    {activeDrilldown === 2 ? (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {items.item_2_poslovnice.breakdown?.length === 0 ? (
                                <p className="muted">{t('Nema evidentiranih zaliha u poslovnicama.', 'No branch stock recorded.')}</p>
                            ) : (
                                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th style={{ padding: '6px 10px' }}>{t('Šifra', 'Code')}</th>
                                            <th style={{ padding: '6px 10px' }}>{t('Poslovnica', 'Branch')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Artikala', 'Items')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Komada', 'Pcs')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Fine XAU (g)', 'Fine XAU (g)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Knjig. (€)', 'Book (€)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Spot (€)', 'Spot (€)')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.item_2_poslovnice.breakdown?.map((b) => (
                                            <tr key={b.warehouse_code} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '6px 10px' }}><code>{b.warehouse_code}</code></td>
                                                <td style={{ padding: '6px 10px' }}>{b.warehouse_name}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{b.item_count}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatNumber(b.qty, locale)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(b.fine_g, locale)} g</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(b.book_eur, locale)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(b.spot_eur, locale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ) : null}

                    {/* Drilldown 3: In transit */}
                    {activeDrilldown === 3 ? (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {items.item_3_na_putu.breakdown?.length === 0 ? (
                                <p className="muted">{t('Nema robe na putu.', 'No goods in transit.')}</p>
                            ) : (
                                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th style={{ padding: '6px 10px' }}>{t('Odredište', 'Destination')}</th>
                                            <th style={{ padding: '6px 10px' }}>SKU</th>
                                            <th style={{ padding: '6px 10px' }}>{t('Naziv artikla', 'Item name')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Količina', 'Qty')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Fine XAU (g)', 'Fine XAU (g)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Vrijednost (€)', 'Value (€)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Spot (€)', 'Spot (€)')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.item_3_na_putu.breakdown?.map((tr, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '6px 10px' }}>
                                                    <code>{tr.warehouse_code}</code> {tr.warehouse_name}
                                                </td>
                                                <td style={{ padding: '6px 10px' }}><code>{tr.item_code}</code></td>
                                                <td style={{ padding: '6px 10px' }}>{tr.item_name || '—'}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatNumber(tr.qty_in_transit, locale)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(tr.fine_g, locale)} g</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(tr.book_eur, locale)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(tr.spot_eur, locale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ) : null}

                    {/* Drilldown 4: Suppliers */}
                    {activeDrilldown === 4 ? (
                        <div>
                            {/* N23 (zahtjev 4): obavezna dnevna rucna potvrda "Roba kod dobavljaca". */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                <div>
                                    {items.item_4_dobavljaci.verification_warning ? (
                                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', background: '#fef3c7', color: '#92400e', fontSize: '0.8rem', fontWeight: 600 }}>
                                            {t('⚠ Nije potvrđeno danas', '⚠ Not verified today')}
                                        </span>
                                    ) : items.item_4_dobavljaci.last_verified_at ? (
                                        <span className="muted" style={{ fontSize: '0.8rem' }}>
                                            {t('Zadnja potvrda:', 'Last verified:')} {formatDateTime(items.item_4_dobavljaci.last_verified_at, locale)}
                                        </span>
                                    ) : null}
                                </div>
                                {data.permissions.can_edit ? (
                                    <button type="button" className="btn btn-primary" style={{ fontSize: '0.8rem' }} disabled={verifyingSuppliers} onClick={() => void verifySuppliers()}>
                                        {verifyingSuppliers ? t('Potvrđujem…', 'Verifying…') : t('Potvrdi danas', 'Verify today')}
                                    </button>
                                ) : null}
                            </div>
                            {items.item_4_dobavljaci.match ? (
                                <p className="muted" style={{ margin: '0 0 0.75rem', fontSize: '0.8rem' }}>
                                    {t(
                                        `Potvrđeni L3: ${items.item_4_dobavljaci.match.confirmed_l3_rows}, upareno s predujmom: ${items.item_4_dobavljaci.match.matched_rows} stavki / ${items.item_4_dobavljaci.match.matched_orders} narudžbi. Preskočeno: nepotvrđeno ${items.item_4_dobavljaci.match.skipped_unconfirmed}, bez otvorenog predujma ${items.item_4_dobavljaci.match.skipped_no_open_order}, bez artikla ${items.item_4_dobavljaci.match.skipped_no_item_match}, količina srezana ${items.item_4_dobavljaci.match.qty_capped_rows}.`,
                                        `Confirmed L3: ${items.item_4_dobavljaci.match.confirmed_l3_rows}, matched to advances: ${items.item_4_dobavljaci.match.matched_rows} lines / ${items.item_4_dobavljaci.match.matched_orders} orders. Skipped: unconfirmed ${items.item_4_dobavljaci.match.skipped_unconfirmed}, no open advance ${items.item_4_dobavljaci.match.skipped_no_open_order}, no item match ${items.item_4_dobavljaci.match.skipped_no_item_match}, qty capped ${items.item_4_dobavljaci.match.qty_capped_rows}.`
                                    )}
                                </p>
                            ) : null}
                            {items.item_4_dobavljaci.breakdown?.length === 0 ? (
                                <p className="muted">{t('Nema ručnog unosa ni potvrđenih L3 narudžbi iz 40.4 uparenih s otvorenim predujmovima.', 'No manual input and no confirmed 40.4 L3 supplier orders matched to open advances.')}</p>
                            ) : (
                                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th style={{ padding: '6px 10px', textAlign: 'center', width: 72 }}>{t('U zbroj', 'In sum')}</th>
                                            <th style={{ padding: '6px 10px' }}>{t('Izvor', 'Source')}</th>
                                            <th style={{ padding: '6px 10px' }}>{t('Naziv / Dobavljač', 'Label / Supplier')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Knjigovodstveno (€)', 'Book (€)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Fine XAU (g)', 'Fine XAU (g)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Spot (€)', 'Spot (€)')}</th>
                                            <th style={{ padding: '6px 10px' }}>{t('Napomena', 'Note')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.item_4_dobavljaci.breakdown?.map((s) => (
                                            <tr key={s.id} style={{ borderBottom: '1px solid #e2e8f0', opacity: s.included_in_sum ? 1 : 0.55 }}>
                                                <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={s.included_in_sum}
                                                        disabled={!data?.permissions.can_edit || togglingId !== null}
                                                        onChange={(e) => void toggleInclude(s.id, e.target.checked)}
                                                        aria-label={t('Ulazi u zbroj pokrivenosti', 'Include in coverage sum')}
                                                    />
                                                </td>
                                                <td style={{ padding: '6px 10px' }}>
                                                    <span style={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', background: s.source === 'plan' ? '#e0f2fe' : '#fef3c7', color: s.source === 'plan' ? '#0369a1' : '#b45309', fontWeight: 600 }}>
                                                        {s.source === 'plan' ? t('40.4 Plan', '40.4 Plan') : t('Ručno', 'Manual')}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '6px 10px', fontWeight: 600 }}>{s.label}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(s.book_eur, locale)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(s.fine_g, locale)} g</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(s.spot_eur, locale)}</td>
                                                <td style={{ padding: '6px 10px', color: '#64748b' }}>{s.note}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ) : null}

                    {/* Drilldown 5: Undelivered */}
                    {activeDrilldown === 5 ? (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>{t('UKUPNO NARUDŽBI', 'TOTAL ORDERS')}</p>
                                    <h3 style={{ margin: '0.3rem 0' }}>{items.item_5_neisporucene.order_count}</h3>
                                    <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>{t('Ugovoreno:', 'Contracted:')} {formatMoney(items.item_5_neisporucene.book_eur, locale)}</p>
                                </div>
                                <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>{t('PLAĆENO U CIJELOSTI', 'PAID IN FULL')}</p>
                                    <h3 style={{ margin: '0.3rem 0', color: '#16a34a' }}>{items.item_5_neisporucene.paid_full_count}</h3>
                                    <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>{formatMoney(items.item_5_neisporucene.paid_full_eur, locale)}</p>
                                </div>
                                <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>{t('PREOSTALO ZA NAPLATITI', 'REMAINING TO COLLECT')}</p>
                                    <h3 style={{ margin: '0.3rem 0', color: '#d97706' }}>{formatMoney(items.item_5_neisporucene.remaining_eur, locale)}</h3>
                                    <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>{t('Kupci duguju za narudžbu', 'Clients owe for orders')}</p>
                                </div>
                                <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>{t('OBVEZA U ZLATU (FINE)', 'GOLD OBLIGATION (FINE)')}</p>
                                    <h3 style={{ margin: '0.3rem 0' }}>{formatFine(items.item_5_neisporucene.fine_g)}</h3>
                                    <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>{t('Po spotu:', 'At spot:')} {formatMoney(items.item_5_neisporucene.spot_eur, locale)}</p>
                                </div>
                                <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>{t('X DANA PROMETA', 'X DAYS OF TURNOVER')}</p>
                                    <h3 style={{ margin: '0.3rem 0' }}>
                                        {items.item_5_neisporucene.days_eur === null ? '—' : `${formatNumber(items.item_5_neisporucene.days_eur, locale)} d`}
                                    </h3>
                                    <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>
                                        {t('Fine:', 'Fine:')} {items.item_5_neisporucene.days_fine === null ? '—' : `${formatNumber(items.item_5_neisporucene.days_fine, locale)} d`}
                                    </p>
                                </div>
                            </div>

                            {/* N23 (zahtjev 3/5): split isporuka klijentu (5a) / pohrana kod nas (5b), po Faros oznaci mjesto_isporuke=POHRANA. */}
                            <div style={{ marginTop: '1.25rem' }}>
                                <strong style={{ fontSize: '0.85rem' }}>{t('Podjela: 5a isporuka / 5b pohrana', 'Split: 5a delivery / 5b storage')}</strong>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                                    {[items.item_5_neisporucene.a_isporuka, items.item_5_neisporucene.b_pohrana].map((split, idx) => (
                                        <div key={idx} style={{ background: '#f8fafc', padding: '0.85rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                            <strong style={{ fontSize: '0.85rem' }}>{split.label}</strong>
                                            <table style={{ width: '100%', minWidth: 0, fontSize: '0.8rem', marginTop: '0.4rem' }}>
                                                <tbody>
                                                    <tr>
                                                        <td className="muted">{t('Broj narudžbi', 'Order count')}</td>
                                                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{split.order_count}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="muted">{t('Preostalo (€)', 'Remaining (€)')}</td>
                                                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(split.remaining_eur, locale)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="muted">{t('Knjigovodstveno (€)', 'Book (€)')}</td>
                                                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(split.book_eur, locale)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="muted">{t('Fine (g)', 'Fine (g)')}</td>
                                                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(split.fine_g, locale)} g</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="muted">{t('Spot (€)', 'Spot (€)')}</td>
                                                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(split.spot_eur, locale)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="muted">{t('X dana prometa', 'X days turnover')}</td>
                                                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{split.days_eur === null ? '—' : `${formatNumber(split.days_eur, locale)} d`}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Drilldown 6a: Lager */}
                    {activeDrilldown === 6.1 ? (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <p className="muted" style={{ margin: '0 0 0.5rem', fontSize: '0.8rem' }}>{items.item_6a_lager.note}</p>
                            {items.item_6a_lager.breakdown?.length === 0 ? (
                                <p className="muted">{t('Nema zaliha u lageru (sve je "smješteno").', 'No lager stock (all is "reserved").')}</p>
                            ) : (
                                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th style={{ padding: '6px 10px' }}>{t('Šifra', 'Code')}</th>
                                            <th style={{ padding: '6px 10px' }}>{t('Poslovnica', 'Branch')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Artikala', 'Items')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Komada', 'Pcs')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Fine XAU (g)', 'Fine XAU (g)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Knjig. (€)', 'Book (€)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Spot (€)', 'Spot (€)')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.item_6a_lager.breakdown?.map((b) => (
                                            <tr key={b.warehouse_code} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '6px 10px' }}><code>{b.warehouse_code}</code></td>
                                                <td style={{ padding: '6px 10px' }}>{b.warehouse_name}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{b.item_count}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatNumber(b.qty, locale)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(b.fine_g, locale)} g</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(b.book_eur, locale)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(b.spot_eur, locale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ) : null}

                    {/* Drilldown 6b: Akcijsko skladiste */}
                    {activeDrilldown === 6.2 ? (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <p className="muted" style={{ margin: '0 0 0.5rem', fontSize: '0.8rem' }}>{items.item_6b_akcijsko.note}</p>
                            {items.item_6b_akcijsko.breakdown?.length === 0 ? (
                                <p className="muted">{t('Nema zaliha u akcijskom skladištu.', 'No stock in the promo warehouse.')}</p>
                            ) : (
                                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th style={{ padding: '6px 10px' }}>SKU</th>
                                            <th style={{ padding: '6px 10px' }}>{t('Naziv artikla', 'Item name')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Količina', 'Qty')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Fine XAU (g)', 'Fine XAU (g)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Knjig. (€)', 'Book (€)')}</th>
                                            <th style={{ padding: '6px 10px', textAlign: 'right' }}>{t('Spot (€)', 'Spot (€)')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.item_6b_akcijsko.breakdown?.map((it, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '6px 10px' }}><code>{it.sku || '—'}</code></td>
                                                <td style={{ padding: '6px 10px' }}>{it.name || '—'}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatNumber(it.qty, locale)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(it.fine_g, locale)} g</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(it.book_eur, locale)}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatMoney(it.spot_eur, locale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* INFORMATIVNI BLOK */}
            <div style={{ marginTop: '2rem', borderTop: '2px dashed #cbd5e1', paddingTop: '1.5rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
                        <h3 style={{ margin: 0 }}>{t('Informativni blok', 'Informative block')}</h3>
                    </div>
                    <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                        {informative.note}
                    </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '1rem', marginBottom: '1.5rem', alignItems: 'start' }}>
                    {/* StoneX */}
                    <div className="card" style={{ padding: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 0, maxWidth: '100%', overflow: 'hidden', position: 'relative', isolation: 'isolate' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                            <strong>{informative.stonex.label}</strong>
                            <span style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>Info</span>
                        </div>
                        <h3 style={{ margin: '0.5rem 0 0.2rem', color: '#0f172a', overflowWrap: 'anywhere' }}>{formatMoney(informative.stonex.spot_eur, locale)}</h3>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                            {formatFine(informative.stonex.fine_g)} {t('ekvivalent', 'equivalent')}
                        </p>
                        {renderIncludeRows(informative.stonex.rows)}
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: '0.4rem' }}>
                            {informative.stonex.note}
                        </p>
                    </div>

                    {/* Potraživanja */}
                    <div className="card" style={{ padding: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 0, maxWidth: '100%', overflow: 'hidden', position: 'relative', isolation: 'isolate' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                            <strong>{informative.potrazivanja.label}</strong>
                            <span style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>Info</span>
                        </div>
                        <h3 style={{ margin: '0.5rem 0 0.2rem', color: '#0f172a', overflowWrap: 'anywhere' }}>{formatMoney(informative.potrazivanja.spot_eur, locale)}</h3>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                            {formatFine(informative.potrazivanja.fine_g)} {t('ekvivalent', 'equivalent')}
                        </p>
                        {renderIncludeRows(informative.potrazivanja.rows)}
                        {informative.potrazivanja.breakdown ? (
                            <div style={{ marginTop: '0.45rem', fontSize: '0.78rem', color: '#64748b' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', minWidth: 0 }}>
                                    <span>{t('Nenaplaćeno po narudžbama (živo)', 'Unpaid on open orders (live)')}</span>
                                    <span style={{ fontFamily: 'monospace', flexShrink: 0 }}>{formatMoney(informative.potrazivanja.breakdown.open_orders_remaining_eur, locale)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', minWidth: 0 }}>
                                    <span>{t('B2B IRA GGSLO + GGM Bugarska (živo)', 'B2B IRA GGSLO + GGM Bulgaria (live)')}</span>
                                    <span style={{ fontFamily: 'monospace', flexShrink: 0 }}>{formatMoney(informative.potrazivanja.breakdown.ira_b2b_open_eur, locale)}</span>
                                </div>
                            </div>
                        ) : null}
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: '0.4rem' }}>
                            {informative.potrazivanja.note}
                        </p>
                    </div>

                    {/* Lom */}
                    <div className="card" style={{ padding: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 0, maxWidth: '100%', overflow: 'hidden', position: 'relative', isolation: 'isolate' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                            <strong>{informative.lom.label}</strong>
                            <span style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>Info</span>
                        </div>
                        <h3 style={{ margin: '0.5rem 0 0.2rem', color: '#0f172a', overflowWrap: 'anywhere' }}>{formatMoney(informative.lom.spot_eur, locale)}</h3>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                            {formatFine(informative.lom.fine_g)} {t('ekvivalent', 'equivalent')}
                        </p>
                        {renderIncludeRows(informative.lom.rows)}
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: '0.4rem' }}>
                            {informative.lom.note}
                        </p>
                    </div>

                    {/* Strana skladišta */}
                    <div className="card" style={{ padding: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 0, maxWidth: '100%', overflow: 'hidden', position: 'relative', isolation: 'isolate' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                            <strong>{informative.strana_skladista.label}</strong>
                            <span style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>Info</span>
                        </div>
                        <h3 style={{ margin: '0.5rem 0 0.2rem', color: '#0f172a', overflowWrap: 'anywhere' }}>{formatMoney(informative.strana_skladista.spot_eur, locale)}</h3>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                            {formatFine(informative.strana_skladista.fine_g)}
                        </p>
                        {renderIncludeRows(informative.strana_skladista.rows)}
                        {renderWarehouseGroups(informative.strana_skladista.groups, t('Nema navedenih skladišta', 'No listed warehouses'))}
                        {informative.strana_skladista.outside && informative.strana_skladista.outside.length > 0 ? (
                            <div style={{ marginTop: '0.6rem', borderTop: '1px dashed #cbd5e1', paddingTop: '0.45rem' }}>
                                <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '0.2rem' }}>
                                    {t('Izvan zbroja', 'Outside subtotal')}
                                </div>
                                {renderWarehouseGroups(informative.strana_skladista.outside, '')}
                            </div>
                        ) : null}
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: '0.4rem' }}>
                            {informative.strana_skladista.note}
                        </p>
                    </div>

                    {/* Gotovina m.6 */}
                    <div className="card" style={{ padding: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 0, maxWidth: '100%', overflow: 'hidden', position: 'relative', isolation: 'isolate' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                            <strong>{informative.gotovina.label}</strong>
                            <span style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>Info</span>
                        </div>
                        <h3 style={{ margin: '0.5rem 0 0.2rem', color: '#0f172a', overflowWrap: 'anywhere' }}>{formatMoney(informative.gotovina.spot_eur, locale)}</h3>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                            {formatFine(informative.gotovina.fine_g)} {t('ekvivalent', 'equivalent')}
                        </p>
                        {renderIncludeRows(informative.gotovina.rows)}
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: '0.4rem' }}>
                            {informative.gotovina.note}
                        </p>
                    </div>

                    {/* Nekurentna roba: poslovnica zaliha bez rezervacije (Vedran 26.08.2026) */}
                    <div className="card" style={{ padding: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 0, maxWidth: '100%', overflow: 'hidden', position: 'relative', isolation: 'isolate' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                            <strong>{t('Nekurentna roba', 'Unmatched stock')}</strong>
                            <span style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>Info</span>
                        </div>
                        <h3 style={{ margin: '0.5rem 0 0.2rem', color: '#0f172a', overflowWrap: 'anywhere' }}>{formatMoney(informative.nekurentna.book_eur, locale)}</h3>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                            {formatFine(informative.nekurentna.fine_g)} {t('ekvivalent', 'equivalent')}
                        </p>
                        {informative.nekurentna.by_warehouse?.length === 0 ? (
                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                                {t('Nema neriješene zalihe u poslovnicama', 'No unmatched branch stock')}
                            </p>
                        ) : (
                            <div style={{ marginTop: '0.5rem' }}>
                                {informative.nekurentna.by_warehouse?.map((w) => (
                                    <div key={w.warehouse_code} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.78rem', color: '#64748b', minWidth: 0, marginBottom: '0.2rem' }}>
                                        <span style={{ overflow: 'hidden', wordBreak: 'break-word' }}>{w.warehouse_code} {w.warehouse_name} ({w.item_count})</span>
                                        <span style={{ fontFamily: 'monospace', flexShrink: 0 }}>{formatMoney(w.book_eur, locale)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: '0.4rem' }}>
                            {t('Zaliha u poslovnici (isti obuhvat kao stavka 2) koja nije rezervirana ni za jednu narudžbu. Ne ulazi u formulu pokrivenosti.', 'Branch stock (same scope as item 2) not reserved for any order. Excluded from the coverage formula.')}
                        </p>
                    </div>
                </div>
            </div>
            </div>

            {/* Modal za uređivanje ručnih unosa */}
            {showVisibility ? (
                <div className="modal-backdrop" role="presentation" onClick={() => setShowVisibility(false)}>
                    <div className="card admin-block" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ width: 'min(620px, 94vw)', maxHeight: '82vh', overflow: 'auto' }}>
                        <h2>{t('Postavke modula 40.6', 'Module 40.6 settings')}</h2>
                        <h3>{t('Pristup', 'Access')}</h3>
                        <p className="muted">{t('View otvara modul. Edit dopušta ručni unos i uključivanje stavki.', 'View opens the module. Edit allows manual entry and item inclusion.')}</p>
                        <input value={accessSearch} onChange={(e) => setAccessSearch(e.target.value)} placeholder={t('Traži osobu ili email...', 'Search person or email...')} style={{ width: '100%' }} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 64px 64px', gap: 8, marginTop: 10, fontWeight: 700 }}><span>{t('Osoba', 'Person')}</span><span>View</span><span>Edit</span></div>
                        <div style={{ display: 'grid', gap: 8, margin: '1rem 0' }}>
                            {visibilityUsers.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(accessSearch.trim().toLowerCase())).slice(0, 80).map((u) => (
                                <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '1fr 64px 64px', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                                    <span><strong>{u.name}</strong><span className="muted" style={{ display: 'block' }}>{u.email}</span></span>
                                    <input aria-label={`View ${u.email}`} type="checkbox" checked={u.visible} disabled={u.locked} onChange={(e) => setVisibilityUsers((rows) => rows.map((row) => row.id === u.id ? { ...row, visible: e.target.checked, editable: e.target.checked ? row.editable : false } : row))} />
                                    <input aria-label={`Edit ${u.email}`} type="checkbox" checked={u.editable} disabled={u.locked} onChange={(e) => setVisibilityUsers((rows) => rows.map((row) => row.id === u.id ? { ...row, editable: e.target.checked, visible: e.target.checked ? true : row.visible } : row))} />
                                </div>
                            ))}
                        </div>
                        <h3>{t('Primatelji maila - Maloprodaja dnevno u 08:00', 'Email recipients - Daily retail at 08:00')}</h3>
                        <input value={recipientSearch} onChange={(e) => setRecipientSearch(e.target.value)} placeholder={t('Traži korisnika...', 'Search user...')} style={{ width: '100%' }} />
                        <div style={{ maxHeight: 180, overflow: 'auto', margin: '8px 0' }}>
                            {recipientUsers.filter((u) => `${u.name ?? ''} ${u.email ?? ''}`.toLowerCase().includes(recipientSearch.trim().toLowerCase())).slice(0, 60).map((u) => <label key={u.id} style={{ display: 'flex', gap: 8, padding: '5px 0' }}><input type="checkbox" checked={recipientIds.has(u.id)} onChange={() => setRecipientIds((old) => { const next = new Set(old); next.has(u.id) ? next.delete(u.id) : next.add(u.id); return next; })} /><span>{u.name || u.email} <span className="muted">{u.email}</span></span></label>)}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}><input value={recipientEmailInput} onChange={(e) => setRecipientEmailInput(e.target.value)} placeholder="email@domena.hr" style={{ flex: 1 }} /><button type="button" className="btn btn-secondary" onClick={() => { const email = recipientEmailInput.trim(); if (/.+@.+\..+/.test(email) && !recipientEmails.includes(email)) setRecipientEmails((old) => [...old, email]); setRecipientEmailInput(''); }}>{t('Dodaj email', 'Add email')}</button></div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>{recipientEmails.map((email) => <button type="button" className="btn btn-secondary" key={email} onClick={() => setRecipientEmails((old) => old.filter((x) => x !== email))}>{email} ×</button>)}</div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button type="button" className="btn btn-secondary" onClick={() => setShowVisibility(false)}>{t('Odustani', 'Cancel')}</button>
                            <button type="button" className="btn btn-primary" disabled={savingVisibility} onClick={() => void saveVisibility()}>{savingVisibility ? t('Spremanje...', 'Saving...') : t('Spremi vidljivost', 'Save visibility')}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            {showManualModal ? (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '1rem',
                    }}
                >
                    <div
                        style={{
                            background: '#ffffff',
                            borderRadius: '8px',
                            maxWidth: '560px',
                            width: '100%',
                            padding: '1.5rem',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                            <h3 style={{ margin: 0 }}>⚙️ {t('Uređivanje ručnih postavki', 'Edit manual settings')}</h3>
                            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowManualModal(false)}>✕</button>
                        </div>

                        <form onSubmit={saveManualInputs}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
                                {manualInputs.map((inp) => (
                                    <div key={inp.key}>
                                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                                            {inp.label} <span className="muted">({inp.unit})</span>
                                        </label>
                                        <input
                                            type={inp.unit === 'tekst' ? 'text' : 'number'}
                                            step={inp.unit === 'tekst' ? undefined : 'any'}
                                            className="input"
                                            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                                            value={manualValues[inp.key] ?? ''}
                                            onChange={(e) => setManualValues({ ...manualValues, [inp.key]: e.target.value })}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => setShowManualModal(false)} disabled={savingManual}>
                                    {t('Odustani', 'Cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={savingManual}>
                                    {savingManual ? t('Spremanje...', 'Saving...') : t('Spremi i ponovno izračunaj', 'Save & recompute')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </section>
    );
}
